import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { fightersSchedule2026, opponentShortNames, monthLabels } from "@/mocks/fightersSchedule";

const DAYS_OF_WEEK = ["日", "月", "火", "水", "木", "金", "土"];

interface ParkingDateRow {
  game_date: string;
  status: string;
}

function formatDateStr(y: number, m: number, d: number) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

const STATUS_LABELS: Record<string, string> = {
  available: "空きあり",
  few: "残りわずか",
  full: "満車",
};

const STATUS_CYCLE: Record<string, string> = {
  available: "few",
  few: "full",
  full: "available",
};

export default function ParkingAdminPage() {
  const today = new Date();
  const todayStr = formatDateStr(today.getFullYear(), today.getMonth() + 1, today.getDate());

  const [viewYear, setViewYear] = useState(2026);
  const [viewMonth, setViewMonth] = useState(() => {
    const m = today.getFullYear() === 2026 ? today.getMonth() + 1 : 3;
    return m;
  });

  const [statusMap, setStatusMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingDate, setSavingDate] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const gamesMap = useMemo(() => {
    const map = new Map<string, string>();
    fightersSchedule2026.forEach((g) => map.set(g.date, g.opponent));
    return map;
  }, []);

  const fetchStatuses = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data, error: fetchError } = await supabase
        .from("parking_dates")
        .select("game_date, status");

      if (fetchError) {
        setError(true);
        return;
      }

      const map = new Map<string, string>();
      (data as ParkingDateRow[]).forEach((row) => {
        map.set(row.game_date, row.status);
      });
      setStatusMap(map);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2500);
  };

  const toggleStatus = async (dateStr: string) => {
    if (savingDate) return;
    const current = statusMap.get(dateStr) || "available";
    const next = STATUS_CYCLE[current] ?? "available";

    setSavingDate(dateStr);
    try {
      const { error: upsertError } = await supabase
        .from("parking_dates")
        .upsert({ game_date: dateStr, status: next }, { onConflict: "game_date" });

      if (upsertError) {
        showToast("更新に失敗しました。もう一度お試しください。");
        return;
      }

      setStatusMap((prev) => {
        const nextMap = new Map(prev);
        nextMap.set(dateStr, next);
        return nextMap;
      });
      showToast(
        `${dateStr.replace(/-/g, "/")} を「${STATUS_LABELS[next] ?? next}」に変更しました`,
      );
    } catch {
      showToast("更新に失敗しました。もう一度お試しください。");
    } finally {
      setSavingDate(null);
    }
  };

  const goToPrevMonth = () => {
    if (viewMonth === 1) {
      setViewYear((y) => y - 1);
      setViewMonth(12);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 12) {
      setViewYear((y) => y + 1);
      setViewMonth(1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const firstDay = new Date(viewYear, viewMonth - 1, 1);
  const startDow = firstDay.getDay();
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();

  const calendarCells: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const fullCount = Array.from(statusMap.values()).filter((s) => s === "full").length;
  const fewCount = Array.from(statusMap.values()).filter((s) => s === "few").length;
  const availableCount = Array.from(statusMap.values()).filter((s) => s === "available").length;

  return (
    <div className="min-h-screen bg-background-100 text-foreground-950">
      <header className="sticky top-0 z-40 bg-background-50 border-b border-background-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-parking-box-fill text-lg" />
            </span>
            <span className="font-heading text-lg text-foreground-950">空き状況管理</span>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            サイトへ戻る
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="text-center">
          <h1 className="font-heading text-2xl md:text-3xl leading-tight text-foreground-950">
            駐車場の空き状況を管理
          </h1>
          <p className="mt-3 text-foreground-600 text-sm md:text-base leading-relaxed">
            試合日の日付をクリックすると、「空きあり」→「残りわずか」→「満車」を順番に切り替えられます。
            変更はホームページに即反映されます。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3">
          <div className="bg-accent-100 rounded-2xl p-4 text-center">
            <div className="text-2xl font-heading text-accent-700">{availableCount}</div>
            <div className="text-xs text-accent-800 mt-0.5">空きあり</div>
          </div>
          <div className="bg-secondary-100 rounded-2xl p-4 text-center">
            <div className="text-2xl font-heading text-secondary-700">{fewCount}</div>
            <div className="text-xs text-secondary-800 mt-0.5">残りわずか</div>
          </div>
          <div className="bg-primary-100 rounded-2xl p-4 text-center">
            <div className="text-2xl font-heading text-primary-700">{fullCount}</div>
            <div className="text-xs text-primary-800 mt-0.5">満車</div>
          </div>
        </div>

        <div className="mt-6 bg-background-50 rounded-3xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
            <button
              onClick={goToPrevMonth}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-600 hover:text-foreground-950 cursor-pointer transition-colors"
              aria-label="前の月"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <span className="font-heading text-lg text-foreground-950">
              {viewYear}年{monthLabels[viewMonth]}
            </span>
            <button
              onClick={goToNextMonth}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-600 hover:text-foreground-950 cursor-pointer transition-colors"
              aria-label="次の月"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>

          <div className="p-4 md:p-6">
            <div className="grid grid-cols-7 mb-2">
              {DAYS_OF_WEEK.map((dow) => (
                <div
                  key={dow}
                  className={`text-center text-[11px] font-medium py-1 ${
                    dow === "日"
                      ? "text-red-500"
                      : dow === "土"
                        ? "text-blue-500"
                        : "text-foreground-500"
                  }`}
                >
                  {dow}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 md:gap-2">
              {calendarCells.map((day, idx) => {
                if (day === null) {
                  return <div key={`empty-${idx}`} className="min-h-[4.5rem] md:min-h-[5rem]" />;
                }
                const dateStr = formatDateStr(viewYear, viewMonth, day);
                const opponent = gamesMap.get(dateStr);
                const isGameDay = Boolean(opponent);
                const isPast = dateStr < todayStr;
                const status = statusMap.get(dateStr);
                const isFull = status === "full";
                const isFew = status === "few";
                const isAvailable = status === "available";
                const isSaving = savingDate === dateStr;
                const shortName = opponent ? (opponentShortNames[opponent] || opponent) : null;

                return (
                  <button
                    key={dateStr}
                    type="button"
                    onClick={() => isGameDay && !isPast && toggleStatus(dateStr)}
                    disabled={!isGameDay || isPast || loading}
                    className={`min-h-[4.5rem] md:min-h-[5rem] flex flex-col items-center justify-center py-1 rounded-xl transition-colors ${
                      isGameDay && !isPast
                        ? "cursor-pointer hover:bg-background-100"
                        : "cursor-default"
                    } ${isFull ? "bg-primary-100/60" : isFew ? "bg-secondary-100/60" : isAvailable ? "bg-accent-100/60" : ""}`}
                  >
                    <span
                      className={`text-sm leading-none ${
                        dateStr === todayStr
                          ? "font-bold text-primary-600"
                          : isPast
                            ? "text-foreground-400"
                            : "text-foreground-800"
                      }`}
                    >
                      {day}
                    </span>
                    {isGameDay && (
                      <span
                        className={`mt-1 text-[10px] leading-tight font-medium rounded-full px-1.5 py-0.5 whitespace-nowrap ${
                          isPast
                            ? "bg-background-200 text-foreground-400"
                            : isFull
                              ? "bg-primary-500 text-background-50"
                              : isFew
                                ? "bg-secondary-500 text-background-50"
                                : isAvailable
                                  ? "bg-accent-500 text-background-50"
                                  : "bg-background-200 text-foreground-500"
                        }`}
                      >
                        {isSaving ? "..." : isPast ? "終了" : isFull ? "満車" : isFew ? "残りわずか" : isAvailable ? "空き" : "未設定"}
                      </span>
                    )}
                    {shortName && (
                      <span className="mt-0.5 text-[10px] leading-tight whitespace-nowrap text-foreground-500">
                        {shortName}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground-700 bg-accent-100 text-accent-800 rounded-full px-3 py-1.5">
            <span className="w-2 h-2 rounded-full bg-accent-500" />
            空きあり
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground-700 bg-secondary-100 text-secondary-800 rounded-full px-3 py-1.5">
            <span className="w-2 h-2 rounded-full bg-secondary-500" />
            残りわずか
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground-700 bg-primary-100 text-primary-800 rounded-full px-3 py-1.5">
            <span className="w-2 h-2 rounded-full bg-primary-500" />
            満車
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500 bg-background-200 rounded-full px-3 py-1.5">
            <span className="w-2 h-2 rounded-full bg-background-400" />
            終了（変更不可）
          </span>
        </div>

        {error && (
          <div className="mt-6 bg-background-50 rounded-2xl p-6 text-center">
            <p className="text-foreground-600 text-sm">データを取得できませんでした。</p>
            <button
              type="button"
              onClick={fetchStatuses}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-secondary-100 text-secondary-800 text-sm font-medium hover:bg-secondary-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              再読み込み
            </button>
          </div>
        )}
      </main>

      {toast && (
        <div className="fixed bottom-6 inset-x-0 flex justify-center px-6 z-50">
          <div className="bg-foreground-900 text-background-50 rounded-full px-5 py-3 text-sm shadow-lg">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}